import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControl, InputLabel, ListItemText, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import geocodingService from '../services/geocodingService';
import indexedDBService from '../services/indexedDBService';
import salesforceService from '../services/salesforceService';

// Fix leaflet default icons in CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const DEFAULT_RADIUS_KM = 10;

const SMS_TEMPLATES = [
  {
    id: 'template1',
    name: 'Template 1 - Direct & Friendly',
    message: 'Hi! We have a client looking for a location in your area that [insert feature or request here]. Do you or someone you know have a property that might suit?\n\nThank you, Pure Locations\n\nReply "unsubscribe" to be removed from our list.'
  },
  {
    id: 'template2',
    name: 'Template 2 - Branded Casual',
    message: 'Pure Locations here — we have a client searching for a property in your area that [insert feature or request here]. Know anyone with something that might fit?\n\nThank you, Pure Locations\n\nReply "unsubscribe" to be removed from our list.'
  },
  {
    id: 'template3',
    name: 'Template 3 - Request Focus',
    message: 'We\'ve got a client request for a location in your area that [insert feature or request here]. Do you or a friend have a property that could work?\n\nThank you, Pure Locations\n\nReply "unsubscribe" to be removed from our list.'
  },
  {
    id: 'template4',
    name: 'Template 4 - Hunt Theme',
    message: 'Hi! We\'re on the hunt for a location in your area that [insert feature or request here] for one of our clients. Could your place — or someone you know\'s — be a match?\n\nThank you, Pure Locations\n\nReply "unsubscribe" to be removed from our list.'
  },
  {
    id: 'template5',
    name: 'Template 5 - Professional',
    message: 'Pure Locations: A client is looking for a location in your area that [insert feature or request here]. If you or someone you know has a suitable property, we\'d love to hear about it!\n\nThank you, Pure Locations\n\nReply "unsubscribe" to be removed from our list.'
  }
];

function FitToMarkers({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (!markers || markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map(m => L.latLng(m.lat, m.lng)));
    map.fitBounds(bounds.pad(0.25));
  }, [markers, map]);
  return null;
}

const Outreach = () => {
  const [flowApiName, setFlowApiName] = useState('sendProspectingSMS');
  const [address, setAddress] = useState('');
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM);
  const [smsMessage, setSmsMessage] = useState('');
  const [center, setCenter] = useState(null); // [lat,lng]
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [searching, setSearching] = useState(false);
  const [locations, setLocations] = useState([]); // Indexed from DB
  const [accountsInRadius, setAccountsInRadius] = useState([]);
  const [sending, setSending] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [availableTiers, setAvailableTiers] = useState([]);
  const [availableDesigns, setAvailableDesigns] = useState([]);
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // Load locations from IndexedDB
  useEffect(() => {
    const load = async () => {
      const locs = await indexedDBService.getAllLocations();
      console.log('Loaded locations:', locs.length, 'sample:', locs.slice(0, 3).map(l => ({ name: l.name, lat: l.latitude, lng: l.longitude })));
      setLocations(locs);
      
      // Extract unique tiers
      const tiers = [...new Set(locs.map(l => l.locationTier).filter(Boolean))];
      
      // Extract unique design keywords by splitting on semicolons
      const designSet = new Set();
      locs.forEach(l => {
        if (l.design) {
          const keywords = l.design.split(';').map(k => k.trim()).filter(Boolean);
          keywords.forEach(k => designSet.add(k));
        }
      });
      const designs = [...designSet].sort();
      
      setAvailableTiers(tiers);
      setAvailableDesigns(designs);
      
      // Select all by default
      setSelectedTiers(tiers);
      setSelectedDesigns(designs);
    };
    load();
  }, []);

  const handleGeocode = async () => {
    if (!address) return;
    setLoadingGeo(true);
    setAccountsInRadius([]); // clear previous results before a new search
    try {
      const coords = await geocodingService.geocodeAddress(address);
      if (coords) setCenter(coords);
    } finally {
      setLoadingGeo(false);
    }
  };


  // Compute accounts in radius using stored lat/lng when available
  useEffect(() => {
    const compute = async () => {
      if (!center || !locations || locations.length === 0) {
        setAccountsInRadius([]);
        return;
      }
      setSearching(true);
      const [cLat, cLng] = center;
      const R = 6371; // km
      const toRad = (deg) => (deg * Math.PI) / 180;
      const results = [];

      console.log('Computing radius search:', { center: [cLat, cLng], radiusKm, totalLocations: locations.length });
      
      for (const loc of locations) {
        // Skip inactive locations
        if (loc.status === 'Inactive Location') {
          continue;
        }
        
        const lat = Number(loc.latitude);
        const lng = Number(loc.longitude);
        if (!isFinite(lat) || !isFinite(lng)) {
          continue;
        }
        const dLat = toRad(lat - cLat);
        const dLng = toRad(lng - cLng);
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(cLat)) * Math.cos(toRad(lat)) * Math.sin(dLng / 2) ** 2;
        const distance = 2 * R * Math.asin(Math.sqrt(a));
        if (distance <= Number(radiusKm || DEFAULT_RADIUS_KM)) {
          results.push({ 
            accountId: loc.accountId, 
            name: loc.name, 
            address: loc.address,
            design: loc.design,
            status: loc.status,
            locationTier: loc.locationTier,
            lat, 
            lng, 
            distanceKm: Number(distance.toFixed(2)) 
          });
        }
      }

      console.log('Radius search results:', results.length, 'locations within', radiusKm, 'km');
      console.log('Sample results:', results.slice(0, 3));
      setAccountsInRadius(results);
      setSearching(false);
    };
    compute();
  }, [center, radiusKm, locations]);

  const filteredAccounts = useMemo(() => {
    let filtered = accountsInRadius;
    
    // Filter by tier
    if (selectedTiers.length > 0) {
      filtered = filtered.filter(a => selectedTiers.includes(a.locationTier));
    }
    
    // Filter by design (contains logic)
    if (selectedDesigns.length > 0) {
      filtered = filtered.filter(a => {
        if (!a.design) return false;
        // Check if the account's design contains any of the selected design keywords
        return selectedDesigns.some(selectedDesign => 
          a.design.toLowerCase().includes(selectedDesign.toLowerCase())
        );
      });
    }
    
    return filtered;
  }, [accountsInRadius, selectedTiers, selectedDesigns]);

  const handleBulkSmsClick = () => {
    if (!flowApiName) {
      alert('Please set the Salesforce Flow API name first');
      return;
    }
    if (!smsMessage || smsMessage.trim().length === 0) {
      alert('Please enter an SMS message');
      return;
    }
    if (!center || filteredAccounts.length === 0) {
      alert('Search a location and ensure there are accounts within radius.');
      return;
    }

    // Show confirmation dialog
    setConfirmDialogOpen(true);
  };

  const handleConfirmSend = async () => {
    setConfirmDialogOpen(false);
    setSending(true);
    
    try {
      // Collect all accountIds into an array
      const accountIds = filteredAccounts.map(a => a.accountId);
      
      // Invoke flow once with collection of accountIds and the message
      // Your Flow will receive:
      // - accountIds: Text Collection
      // - smsMessage: Text
      // Use a Loop element to iterate over accountIds
      await salesforceService.invokeFlow(flowApiName, [{ 
        accountIds: accountIds,
        smsMessage: smsMessage
      }]);
      
      alert(`✅ Flow invoked successfully for ${filteredAccounts.length} location(s)!`);
    } catch (e) {
      console.error('Flow error', e);
      alert(`Flow failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleCancelSend = () => {
    setConfirmDialogOpen(false);
  };

  const mapMarkers = useMemo(() => {
    return (filteredAccounts || []).map(a => ({ lat: a.lat, lng: a.lng }));
  }, [filteredAccounts]);

  const columns = [
    { field: 'name', headerName: 'Location Name', flex: 1, minWidth: 200 },
    { field: 'address', headerName: 'Address', flex: 1, minWidth: 200 },
    { field: 'design', headerName: 'Design', width: 120 },
    { field: 'status', headerName: 'Status', width: 130 },
    { field: 'locationTier', headerName: 'Tier', width: 100 },
    { field: 'distanceKm', headerName: 'Distance (km)', width: 120, type: 'number' }
  ];

  const rows = filteredAccounts.map(a => ({
    id: a.accountId,
    accountId: a.accountId,
    name: a.name,
    address: a.address,
    design: a.design,
    status: a.status,
    locationTier: a.locationTier,
    distanceKm: a.distanceKm
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 2 }}>
        <Card>
          <CardContent>
          <Typography variant="h6" gutterBottom>Prospecting</Typography>
          <Stack spacing={2}>
            <TextField label="Address or suburb" value={address} onChange={(e) => setAddress(e.target.value)} size="small" onKeyDown={(e)=>{ if(e.key==='Enter') handleGeocode(); }} />
            <TextField label="Radius (km)" type="number" value={radiusKm} onChange={(e) => setRadiusKm(e.target.value)} size="small" />
            <FormControl size="small" fullWidth>
              <InputLabel id="tier-filter-label">Tier</InputLabel>
              <Select
                labelId="tier-filter-label"
                label="Tier"
                multiple
                value={selectedTiers}
                onChange={(e) => {
                  const value = e.target.value;
                  // Check if __SELECT_ALL__ was clicked
                  if (value.includes('__SELECT_ALL__')) {
                    // Toggle: if all are selected, deselect all; otherwise select all
                    if (selectedTiers.length === availableTiers.length) {
                      setSelectedTiers([]);
                    } else {
                      setSelectedTiers(availableTiers);
                    }
                  } else {
                    // Normal selection
                    setSelectedTiers(value);
                  }
                }}
                renderValue={(selected) => {
                  if (selected.length === availableTiers.length) return 'All';
                  if (selected.length === 0) return 'None';
                  return `${selected.length} of ${availableTiers.length} selected`;
                }}
              >
                <MenuItem value="__SELECT_ALL__" sx={{ backgroundColor: 'action.hover', fontWeight: 'bold' }}>
                  <Checkbox
                    checked={selectedTiers.length === availableTiers.length}
                    indeterminate={selectedTiers.length > 0 && selectedTiers.length < availableTiers.length}
                  />
                  <ListItemText primary={selectedTiers.length === availableTiers.length ? "Deselect All" : "Select All"} />
                </MenuItem>
                {availableTiers.map((tier) => (
                  <MenuItem key={tier} value={tier}>
                    <Checkbox checked={selectedTiers.indexOf(tier) > -1} />
                    <ListItemText primary={tier} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="design-filter-label">Design</InputLabel>
              <Select
                labelId="design-filter-label"
                label="Design"
                multiple
                value={selectedDesigns}
                onChange={(e) => {
                  const value = e.target.value;
                  // Check if __SELECT_ALL__ was clicked
                  if (value.includes('__SELECT_ALL__')) {
                    // Toggle: if all are selected, deselect all; otherwise select all
                    if (selectedDesigns.length === availableDesigns.length) {
                      setSelectedDesigns([]);
                    } else {
                      setSelectedDesigns(availableDesigns);
                    }
                  } else {
                    // Normal selection
                    setSelectedDesigns(value);
                  }
                }}
                renderValue={(selected) => {
                  if (selected.length === availableDesigns.length) return 'All';
                  if (selected.length === 0) return 'None';
                  return `${selected.length} of ${availableDesigns.length} selected`;
                }}
              >
                <MenuItem value="__SELECT_ALL__" sx={{ backgroundColor: 'action.hover', fontWeight: 'bold' }}>
                  <Checkbox
                    checked={selectedDesigns.length === availableDesigns.length}
                    indeterminate={selectedDesigns.length > 0 && selectedDesigns.length < availableDesigns.length}
                  />
                  <ListItemText primary={selectedDesigns.length === availableDesigns.length ? "Deselect All" : "Select All"} />
                </MenuItem>
                {availableDesigns.map((design) => (
                  <MenuItem key={design} value={design}>
                    <Checkbox checked={selectedDesigns.indexOf(design) > -1} />
                    <ListItemText primary={design} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleGeocode} disabled={loadingGeo || searching}>{(loadingGeo || searching) ? 'Searching...' : 'Find'}</Button>
            <Divider />
            <Typography variant="body2">Accounts in radius: {filteredAccounts.length}</Typography>
            <FormControl size="small" fullWidth>
              <InputLabel id="flow-label">Salesforce Flow</InputLabel>
              <Select
                labelId="flow-label"
                label="Salesforce Flow"
                value={flowApiName}
                onChange={(e) => setFlowApiName(e.target.value)}
              >
                <MenuItem value="sendProspectingSMS">Send Prospecting SMS</MenuItem>
                <MenuItem value="sendProspectingEmail">Send Prospecting Email</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="template-label">SMS Template (Optional)</InputLabel>
              <Select
                labelId="template-label"
                label="SMS Template (Optional)"
                value={selectedTemplate}
                onChange={(e) => {
                  const templateId = e.target.value;
                  setSelectedTemplate(templateId);
                  if (templateId) {
                    const template = SMS_TEMPLATES.find(t => t.id === templateId);
                    if (template) {
                      setSmsMessage(template.message);
                    }
                  }
                }}
              >
                <MenuItem value="">
                  <em>None - Custom Message</em>
                </MenuItem>
                {SMS_TEMPLATES.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField 
              label="SMS Message" 
              value={smsMessage} 
              onChange={(e) => {
                setSmsMessage(e.target.value);
                // Clear template selection if user manually edits
                if (selectedTemplate) {
                  const template = SMS_TEMPLATES.find(t => t.id === selectedTemplate);
                  if (template && e.target.value !== template.message) {
                    setSelectedTemplate('');
                  }
                }
              }} 
              multiline 
              rows={5} 
              size="small"
              placeholder="Select a template above and customize [insert feature or request here], or write your own message..."
              helperText={`${smsMessage.length} characters. Remember to replace [insert feature or request here] with specific details.`}
            />
            <Button variant="contained" color="secondary" onClick={handleBulkSmsClick} disabled={sending || !flowApiName || filteredAccounts.length===0 || !smsMessage}>
              {sending ? `Sending...` : 'Send Bulk SMS'}
            </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ height: 600, p: 0 }}>
            <Box sx={{ height: 600 }}>
              <MapContainer center={center || [-33.8688, 151.2093]} zoom={center ? 11 : 5} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {center && (
                  <>
                    <Marker position={center}>
                      <Popup>Search Center</Popup>
                    </Marker>
                    <Circle center={center} radius={(Number(radiusKm || DEFAULT_RADIUS_KM)) * 1000} pathOptions={{ color: '#1976d2', fillOpacity: 0.1 }} />
                  </>
                )}
              {filteredAccounts.length > 0 && filteredAccounts.map(a => {
                return (
                  <Marker key={a.accountId} position={[a.lat, a.lng]}>
                    <Popup>
                      <div>
                        <strong>{a.name}</strong><br/>
                        <small>{a.address}</small><br/>
                        <small>Design: {a.design}</small><br/>
                        <small>Status: {a.status}</small><br/>
                        <small>Tier: {a.locationTier}</small><br/>
                        <small><strong>{a.distanceKm}km away</strong></small>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
                {mapMarkers.length > 0 && <FitToMarkers markers={mapMarkers} />}
              </MapContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Results DataGrid */}
      {filteredAccounts.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Search Results ({filteredAccounts.length} locations)</Typography>
            <Box sx={{ height: 500, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50, 100]}
                disableSelectionOnClick
                density="compact"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelSend}>
        <DialogTitle>Confirm Bulk SMS</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will send a bulk SMS to all Account Main Contacts from the results list ({filteredAccounts.length} location{filteredAccounts.length !== 1 ? 's' : ''}).
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSend} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmSend} variant="contained" color="secondary" autoFocus>
            Send SMS
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Outreach;


